@if (initialized) {
    <div class="flex flex-column gap-3">

        <!-- Operation + Entity-Type -->
        <p-card>
            <div class="grid">
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-2">
                        <label class="font-semibold text-color">Operation</label>
                        <p-select
                                [options]="operationOptions"
                                optionLabel="label"
                                optionValue="value"
                                appendTo="body"
                                styleClass="w-full"
                                [ngModel]="selectedOperation"
                                (ngModelChange)="onOperationChange($event)">
                        </p-select>
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="flex flex-column gap-2">
                        <label class="font-semibold text-color">Entity-Typ</label>
                        <p-select
                                [options]="entityTypeOptions"
                                optionLabel="label"
                                optionValue="value"
                                [filter]="true"
                                [filterBy]="'label,value'"
                                [showClear]="true"
                                appendTo="body"
                                styleClass="w-full"
                                placeholder="Entity-Typ auswählen"
                                [ngModel]="selectedEntityType"
                                (ngModelChange)="onEntityTypeChange($event)">
                        </p-select>
                    </div>
                </div>
            </div>
        </p-card>

        <!-- Lookup (UPDATE / DELETE / READ) -->
        @if (requiresLookup()) {
            <p-card>
                <div class="px-3 pt-3 font-semibold text-color">Lookup</div>
                <div class="grid">
                    <div class="col-12 md:col-4">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Lookup-Typ</label>
                            <p-select
                                    [options]="lookupByOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full"
                                    [ngModel]="lookupBy"
                                    (ngModelChange)="onLookupByChange($event)">
                            </p-select>
                        </div>
                    </div>
                    <div class="col-12 md:col-8">
                        @if (lookupBy === CrudLookupByEnum.ID) {
                            <div class="flex flex-column gap-2">
                                <label class="font-semibold text-color">ID-Quelle (Variable oder Literal)</label>
                                <input pInputText
                                       class="w-full"
                                       placeholder="z.B. contractId"
                                       [ngModel]="idSource"
                                       (ngModelChange)="onIdSourceChange($event)"/>
                                <small class="text-color-secondary">
                                    Bezeichner werden als Variable gespeichert,
                                    alles andere als Literal-Wert.
                                </small>
                            </div>
                        } @else {
                            <div class="flex flex-column gap-2">
                                <label class="font-semibold text-color">Kriterien</label>
                                @for (row of criteriaRows; track $index) {
                                    <div class="grid align-items-center">
                                        <div class="col-12 md:col-5">
                                            <input pInputText
                                                   class="w-full"
                                                   placeholder="Feldname"
                                                   [(ngModel)]="row.field"
                                                   (ngModelChange)="onCriteriaRowChange()"/>
                                        </div>
                                        <div class="col-12 md:col-6">
                                            <input pInputText
                                                   class="w-full"
                                                   placeholder="Wert oder Variable"
                                                   [(ngModel)]="row.value"
                                                   (ngModelChange)="onCriteriaRowChange()"/>
                                        </div>
                                        <div class="col-12 md:col-1 text-right">
                                            <p-button icon="fa-regular fa-xmark"
                                                      severity="danger"
                                                      [text]="true"
                                                      (click)="removeCriteriaRow($index)">
                                            </p-button>
                                        </div>
                                    </div>
                                }
                                <div>
                                    <p-button icon="fa-regular fa-plus"
                                              label="Kriterium hinzufügen"
                                              [outlined]="true"
                                              (click)="addCriteriaRow()">
                                    </p-button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </p-card>
        }

        <!-- UPDATE: versionSource + nullSemantik -->
        @if (isUpdate()) {
            <p-card>
                <div class="px-3 pt-3 font-semibold text-color">Optimistic Lock &amp; Null-Semantik</div>
                <div class="grid">
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">versionSource (optional)</label>
                            <input pInputText
                                   class="w-full"
                                   placeholder="z.B. expectedVersion"
                                   [ngModel]="versionSource"
                                   (ngModelChange)="onVersionSourceChange($event)"/>
                        </div>
                    </div>
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Null-Semantik</label>
                            <p-select
                                    [options]="nullSemanticOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full"
                                    [ngModel]="nullSemantic"
                                    (ngModelChange)="onNullSemanticChange($event)">
                            </p-select>
                        </div>
                    </div>
                </div>
                @if (showUpdateWithoutVersionWarning) {
                    <div class="flex align-items-start gap-2 mt-3 text-orange-500">
                        <i class="fa-regular fa-triangle-exclamation mt-1"></i>
                        <div>
                            <div class="font-semibold">Kein Optimistic Lock</div>
                            <div class="text-color-secondary">
                                Ohne <code>versionSource</code> kann Phantom-Update nicht erkannt werden.
                                Empfohlen wird das Setzen einer Versionsvariablen.
                            </div>
                        </div>
                    </div>
                }
            </p-card>
        }

        <!-- DELETE: softDelete -->
        @if (isDelete()) {
            <p-card>
                <div class="px-3 pt-3 font-semibold text-color">Löschverhalten</div>
                <div class="grid">
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Soft-Delete</label>
                            <p-select
                                    [options]="softDeleteOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full"
                                    [ngModel]="softDelete"
                                    (ngModelChange)="onSoftDeleteChange($event)">
                            </p-select>
                        </div>
                    </div>
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">versionSource (optional)</label>
                            <input pInputText
                                   class="w-full"
                                   placeholder="z.B. expectedVersion"
                                   [ngModel]="versionSource"
                                   (ngModelChange)="onVersionSourceChange($event)"/>
                        </div>
                    </div>
                </div>
            </p-card>
        }

        <!-- READ: limit -->
        @if (isRead()) {
            <p-card>
                <div class="px-3 pt-3 font-semibold text-color">Read-Optionen</div>
                <div class="grid">
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Limit (optional)</label>
                            <input pInputText
                                   type="number"
                                   class="w-full"
                                   placeholder="z.B. 10"
                                   [ngModel]="readLimit"
                                   (ngModelChange)="onReadLimitChange($event)"/>
                        </div>
                    </div>
                </div>
            </p-card>
        }

        <!-- Binding-Mode + Bulk Source -->
        @if (requiresFieldMappings()) {
            <p-card>
                <div class="px-3 pt-3 font-semibold text-color">Modus &amp; Mapping</div>
                <div class="grid">
                    <div class="col-12 md:col-4">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Modus</label>
                            <p-select
                                    [options]="bindingOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full"
                                    [ngModel]="selectedBinding"
                                    (ngModelChange)="onBindingChange($event)">
                            </p-select>
                        </div>
                    </div>
                    <div class="col-12 md:col-4">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">FK-Resolution</label>
                            <p-select
                                    [options]="fkResolutionOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full"
                                    [ngModel]="selectedFkResolution"
                                    (ngModelChange)="onFkResolutionChange($event)">
                            </p-select>
                        </div>
                    </div>
                </div>

                @if (requiresBulkSource()) {
                    <div class="grid mt-3">
                        <div class="col-12 md:col-6">
                            <div class="flex flex-column gap-2">
                                <label class="font-semibold text-color">bulkSource (Variable) *</label>
                                <input pInputText
                                       class="w-full"
                                       placeholder="z.B. contractDto"
                                       [ngModel]="bulkSource"
                                       (ngModelChange)="onBulkSourceChange($event)"/>
                            </div>
                        </div>
                        <div class="col-12 md:col-6">
                            <div class="flex flex-column gap-2">
                                <label class="font-semibold text-color">bulkAllowedFields *</label>
                                <input pInputText
                                       class="w-full"
                                       placeholder="z.B. status, endDate, remarks"
                                       [ngModel]="bulkAllowedFields.join(', ')"
                                       (ngModelChange)="onBulkAllowedFieldsInput($event)"/>
                                <small class="text-color-secondary">Komma-separierte Whitelist.</small>
                            </div>
                        </div>
                    </div>
                    @if (showMissingAllowListWarning) {
                        <div class="flex align-items-start gap-2 mt-3 text-orange-500">
                            <i class="fa-regular fa-triangle-exclamation mt-1"></i>
                            <div>
                                <div class="font-semibold">Pflichtangabe fehlt</div>
                                <div class="text-color-secondary">
                                    Bei <strong>BULK_DTO</strong> und <strong>HYBRID</strong> sind
                                    <code>bulkSource</code> UND <code>bulkAllowedFields</code> Pflicht.
                                </div>
                            </div>
                        </div>
                    }
                }

                <!-- Field-Mapping-Editor -->
                <div class="mt-4">
                    <div class="flex align-items-center justify-content-between mb-2">
                        <span class="font-semibold text-color">Field-Mappings</span>
                        <p-button icon="fa-regular fa-plus"
                                  label="Mapping hinzufügen"
                                  [outlined]="true"
                                  (click)="addFieldMappingRow()">
                        </p-button>
                    </div>

                    @if (fieldMappings.length === 0) {
                        <div class="text-color-secondary">Keine Mappings konfiguriert.</div>
                    } @else {
                        <div class="flex flex-column gap-3">
                            @for (row of fieldMappings; track $index) {
                                <div class="surface-100 border-round p-3">
                                    <div class="grid align-items-start">
                                        <div class="col-12 md:col-3">
                                            <label class="font-semibold text-color">Pfad</label>
                                            <input pInputText
                                                   class="w-full"
                                                   placeholder="z.B. firstName"
                                                   [(ngModel)]="row.path"
                                                   (ngModelChange)="onFieldMappingChange()"/>
                                            @if (row.path && isFieldBlocked(row.path)) {
                                                <small class="text-red-500">
                                                    Feld ist global blockiert.
                                                </small>
                                            }
                                        </div>
                                        <div class="col-12 md:col-2">
                                            <label class="font-semibold text-color">Source</label>
                                            <p-select
                                                    [options]="sourceModeOptions"
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    appendTo="body"
                                                    styleClass="w-full"
                                                    [(ngModel)]="row.sourceMode"
                                                    (ngModelChange)="onFieldMappingChange()">
                                            </p-select>
                                        </div>
                                        <div class="col-12 md:col-3">
                                            <label class="font-semibold text-color">
                                                @if (row.sourceMode === 'variable') {
                                                    Variable
                                                } @else {
                                                    Wert
                                                }
                                            </label>
                                            @if (row.sourceMode === 'variable' && variables && variables.length > 0) {
                                                <p-select
                                                        [options]="variables"
                                                        appendTo="body"
                                                        styleClass="w-full"
                                                        placeholder="Variable wählen"
                                                        [filter]="true"
                                                        [(ngModel)]="row.variableName"
                                                        (ngModelChange)="onFieldMappingChange()">
                                                </p-select>
                                            }
                                            @if (row.sourceMode === 'variable' && (!variables || variables.length === 0)) {
                                                <input pInputText
                                                       class="w-full"
                                                       placeholder="Variablen-Name"
                                                       [(ngModel)]="row.variableName"
                                                       (ngModelChange)="onFieldMappingChange()"/>
                                            }
                                            @if (row.sourceMode === 'literal') {
                                                <input pInputText
                                                       class="w-full"
                                                       placeholder="Literal-Wert"
                                                       [(ngModel)]="row.literalValue"
                                                       (ngModelChange)="onFieldMappingChange()"/>
                                            }
                                        </div>
                                        <div class="col-6 md:col-1 flex align-items-center gap-2 mt-4">
                                            <p-checkbox [binary]="true" [(ngModel)]="row.mandatory"></p-checkbox>
                                            <label class="text-color">Pflicht</label>
                                        </div>
                                        <div class="col-6 md:col-2">
                                            <label class="font-semibold text-color">Default</label>
                                            <input pInputText
                                                   class="w-full"
                                                   placeholder="optional"
                                                   [(ngModel)]="row.defaultValue"
                                                   (ngModelChange)="onFieldMappingChange()"/>
                                        </div>
                                        <div class="col-12 md:col-1 text-right mt-4">
                                            <p-button icon="fa-regular fa-xmark"
                                                      severity="danger"
                                                      [text]="true"
                                                      (click)="removeFieldMappingRow($index)">
                                            </p-button>
                                        </div>
                                    </div>
                                    @if (isLikelyFkPath(row.path)) {
                                        <div class="grid mt-2">
                                            <div class="col-12 md:col-6">
                                                <label class="font-semibold text-color">FK-Resolution (Override)</label>
                                                <p-select
                                                        [options]="fkResolutionOptions"
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        appendTo="body"
                                                        styleClass="w-full"
                                                        [showClear]="true"
                                                        placeholder="aus Operation übernehmen"
                                                        [(ngModel)]="row.fkResolution"
                                                        (ngModelChange)="onFieldMappingChange()">
                                                </p-select>
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }

                    @if (showBlockedFieldsWarning) {
                        <div class="flex align-items-start gap-2 mt-3 text-red-500">
                            <i class="fa-regular fa-triangle-exclamation mt-1"></i>
                            <div>
                                <div class="font-semibold">Blockierte Felder erkannt</div>
                                <div class="text-color-secondary">
                                    Folgende Pfade sind in den globalen Blocklisten enthalten und werden vom
                                    Backend abgelehnt: <strong>{{ blockedFieldPaths.join(', ') }}</strong>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </p-card>
        }

        <!-- Exports -->
        <p-card>
            <div class="px-3 pt-3 font-semibold text-color">Exports</div>
            <div class="flex flex-column gap-2">
                <label class="font-semibold text-color">Standard-Exports</label>
                <p-multiSelect
                        [options]="exportOptions"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        appendTo="body"
                        styleClass="w-full"
                        placeholder="alle anwendbaren Exports übernehmen"
                        [ngModel]="selectedExports"
                        (ngModelChange)="onExportsChange($event)">
                </p-multiSelect>
                <small class="text-color-secondary">
                    Ohne Auswahl liefert das Backend alle für die Operation anwendbaren Exports.
                </small>
            </div>
        </p-card>

    </div>
}
